import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  CircularProgress,
  IconButton,
  Box,
  Alert,
  Autocomplete,
  Chip,
  MenuItem,
  InputAdornment,
  InputLabel,
  Select
} from '@mui/material';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { app } from '../../../../firebase.config';
import { Close as CloseIcon, Visibility, VisibilityOff } from '@mui/icons-material';

const CreateUser = ({ open, onClose, onSuccess, existingUsernames = [], editMode = false, userData = null }) => {
  // Form states
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [role, setRole] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]);

  // Other states
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Get token from localStorage
  const token = () => {
    return localStorage.getItem('token');
  };

  // Load stores when component mounts and populate form if in edit mode
  useEffect(() => {
    if (open) {
      fetchStores();

      if (editMode && userData) {
        // Populate form with user data for editing
        setFullName(userData.name || '');
        setEmail(userData.email || '');
        setNumber(userData.number || '');
        setRole(userData.role || '');
        setUsername(userData.username || '');
        // Don't set password in edit mode
        // We'll fetch and set stores after they're loaded
      } else {
        // Reset form for create mode
        resetForm();
      }
    }
    // eslint-disable-next-line
  }, [open, editMode, userData]);

  // Set selected stores after stores are loaded (for edit mode)
  useEffect(() => {
    if (editMode && userData && stores.length > 0 && userData.stores) {
      // Find the store objects that match the user's store IDs
      const userStores = stores.filter((store) =>
        userData.stores.some((userStore) => (typeof userStore === 'string' ? userStore === store._id : userStore._id === store._id))
      );
      setSelectedStores(userStores);
    }
  }, [stores, editMode, userData]);

  // Fetch stores from API
  const fetchStores = async () => {
    setStoresLoading(true);
    try {
      const response = await axios.post('https://db.neophyte.live/sentinel-api/api/sentinel/getStores', {
        user_id: '',
        headers: {
          'Content-Type': 'application/json',
          Authorization: await token()
        }
      });

      if (response.data && response.data.stores) {
        setStores(response.data.stores);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      setError('Failed to fetch stores. Please try again.');
    } finally {
      setStoresLoading(false);
    }
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};

    if (!fullName.trim()) errors.fullName = 'Full Name is required';

    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Invalid email format';

    if (!number.trim()) errors.number = 'Number is required';
    else if (!/^\d{10}$/.test(number)) errors.number = 'Number must be 10 digits';

    if (!role) errors.role = 'Role is required';

    if (selectedStores.length === 0) errors.stores = 'At least one store must be selected';

    if (!username.trim()) errors.username = 'Username is required';
    else if (!editMode && existingUsernames.includes(username)) errors.username = 'Username already exists';
    // Only check for existing username in create mode, not edit mode

    // Only validate password in create mode, not edit mode
    if (!editMode) {
      if (!password.trim()) errors.password = 'Password is required';
      else if (password.length < 6) errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle role change
  const handleRoleChange = (e) => {
    setRole(e.target.value);
    // Reset selected stores when role changes
    setSelectedStores([]);
  };

  // Handle select all stores
  const handleSelectAllStores = () => {
    if (selectedStores.length === stores.length) {
      // If all stores are already selected, deselect all
      setSelectedStores([]);
    } else {
      // Otherwise select all stores
      setSelectedStores([...stores]);
    }
  };

  // Get options for the store dropdown, including Select All option for NHQ role
  const getStoreOptions = () => {
    if (role === 'NHQ') {
      // Add a special option at the beginning for Select All
      return [{ _id: 'select-all', name: 'Select All Stores' }, ...stores];
    }
    return stores;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    // Prepare data for API call
    const userDataToSend = {
      full_name: fullName,
      email: email,
      number: number,
      role: role,
      username: username,
      stores: selectedStores
        .filter((store) => store._id !== 'select-all') // Filter out the Select All option
        .map((store) => store._id) // Send only store IDs
    };

    // Add password only for new users, not when editing
    if (!editMode) {
      const auth = getAuth(app);

      userDataToSend.hashed_password = password; // API expects 'hashed_password' instead of 'password'

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Add Firebase UID to your user data
      userDataToSend.firebase_uid = firebaseUser.uid;
      userDataToSend.hashed_password = password;
    }

    console.log(`User data to be ${editMode ? 'updated' : 'created'}:`, userDataToSend);

    try {
      // Get the user ID for update mode
      const userId = editMode && userData ? userData.id : null;
      console.log('User ID for update:', userId);

      const url = editMode
        ? `https://db.neophyte.live/sentinel-api/api/sentinel/update/${userId}`
        : 'https://db.neophyte.live/sentinel-api/api/sentinel/create';

      const response = await axios.post(url, userDataToSend, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: await token()
        }
      });

      if (response.data) {
        console.log(`User ${editMode ? 'updated' : 'created'} successfully:`, response.data);
        onSuccess();
        resetForm();
      }
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} user:`, error);
      setError(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} user. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Reset form
  const resetForm = () => {
    setFullName('');
    setEmail('');
    setNumber('');
    setRole('');
    setUsername('');
    setPassword('');
    setSelectedStores([]);
    setFormErrors({});
    setShowPassword(false);
    setError(null);
  };

  // Handle dialog close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '0',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '20px',
          pb: 2,
          pt: 2,
          px: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {editMode ? 'Edit User Details' : 'Create New User'}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3, mb: 3 }}>
          {/* Full Name */}
          <TextField
            label="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            error={!!formErrors.fullName}
            helperText={formErrors.fullName}
            required
          />

          {/* Email */}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            error={!!formErrors.email}
            helperText={formErrors.email}
            required
          />

          {/* Number */}
          <TextField
            label="Number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            fullWidth
            error={!!formErrors.number}
            helperText={formErrors.number}
            required
          />

          {/* Role */}
          <FormControl fullWidth error={!!formErrors.role} required>
            <InputLabel>Role</InputLabel>
            <Select value={role} onChange={handleRoleChange} label="Role">
              <MenuItem value="NHQ">NHQ</MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
            </Select>
            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
          </FormControl>

          {/* Username */}
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            error={!!formErrors.username}
            helperText={formErrors.username}
            required
          />

          {/* Password - Only show in create mode */}
          {!editMode && (
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              error={!!formErrors.password}
              helperText={formErrors.password}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleTogglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}
        </Box>

        {/* Stores */}
        <FormControl fullWidth error={!!formErrors.stores} required sx={{ mb: 2 }}>
          <Autocomplete
            multiple={role === 'NHQ'}
            options={getStoreOptions()}
            loading={storesLoading}
            getOptionLabel={(option) => option.name}
            value={selectedStores}
            disabled={!role} // Disable until role is selected
            onChange={(e, newValue) => {
              // Check if Select All option was clicked (for NHQ role)
              if (role === 'NHQ' && newValue.some((item) => item._id === 'select-all')) {
                handleSelectAllStores();
                return;
              }

              // If role is Manager, only allow one store
              if (role === 'Manager' && newValue.length > 1) {
                setSelectedStores([newValue[newValue.length - 1]]);
              } else {
                setSelectedStores(newValue);
              }
            }}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stores"
                error={!!formErrors.stores}
                helperText={formErrors.stores}
                placeholder={!role ? 'Select a role first' : role === 'NHQ' ? 'Select multiple stores' : 'Select one store'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {storesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value
                .filter((option) => option._id !== 'select-all') // Filter out the Select All option
                .map((option, index) => (
                  <Chip
                    key={option._id}
                    label={option.name}
                    {...getTagProps({ index })}
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      color: '#1976d2',
                      fontWeight: 500
                    }}
                  />
                ))
            }
          />
          <FormHelperText>
            {!role ? 'Please select a role first' : role === 'NHQ' ? 'Select multiple stores' : 'Select one store'}
          </FormHelperText>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button onClick={handleClose} color="inherit" sx={{ borderRadius: '8px', mr: 1 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" sx={{ borderRadius: '8px' }} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : editMode ? 'Update User' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUser;
